/**
 * Business Document Upload Card Component
 * 
 * A card component for uploading required business documents for vendor verification.
 * Supports drag-and-drop, file validation, and expiry date tracking (for CGL insurance).
 */

import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { format, differenceInDays, parseISO } from 'date-fns';
import {
  Upload,
  FileText,
  CheckCircle,
  X,
  Download,
  RefreshCw,
  CalendarIcon,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import type { BusinessDocumentType } from '@/hooks/useBusinessDocuments';

interface BusinessDocumentUploadCardProps {
  documentType: BusinessDocumentType;
  title: string;
  description: string;
  existingDocumentUrl: string | null;
  uploadedAt: string | null;
  expiryDate?: string | null;
  requiresExpiryDate?: boolean;
  isUploading?: boolean;
  disabled?: boolean;
  onUpload: (file: File, expiryDate?: Date) => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  onView?: () => void;
}

export function BusinessDocumentUploadCard({
  documentType,
  title,
  description,
  existingDocumentUrl,
  uploadedAt,
  expiryDate,
  requiresExpiryDate = false,
  isUploading = false,
  disabled = false,
  onUpload,
  onRemove,
  onView,
}: BusinessDocumentUploadCardProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedExpiryDate, setSelectedExpiryDate] = useState<Date | undefined>(undefined);
  const [isLocalUploading, setIsLocalUploading] = useState(false);

  const hasDocument = Boolean(existingDocumentUrl);

  // Check if expiry is approaching (within 30 days)
  const expiryWarning = expiryDate ? differenceInDays(parseISO(expiryDate), new Date()) <= 30 : false;
  const isExpired = expiryDate ? differenceInDays(parseISO(expiryDate), new Date()) < 0 : false;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragActive(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  }, [disabled]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
    // Reset input to allow re-selecting the same file
    e.target.value = '';
  }, []);

  const handleFileSelection = (file: File) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Validate expiry date if required
    if (requiresExpiryDate && !selectedExpiryDate) {
      return;
    }

    setIsLocalUploading(true);
    const success = await onUpload(selectedFile, selectedExpiryDate);
    setIsLocalUploading(false);

    if (success) {
      setSelectedFile(null);
      setSelectedExpiryDate(undefined);
    }
  };

  const handleRemove = async () => {
    const success = await onRemove();
    if (success) {
      setSelectedFile(null);
      setSelectedExpiryDate(undefined);
    }
  };

  const cancelSelection = () => {
    setSelectedFile(null);
    setSelectedExpiryDate(undefined);
  };

  const uploading = isUploading || isLocalUploading;

  return (
    <Card className={cn(
      'relative transition-all h-full flex flex-col',
      hasDocument && 'border-l-4 border-l-green-500',
      isDragActive && 'border-primary border-dashed bg-primary/5',
      disabled && 'opacity-60'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{title}</CardTitle>
              {hasDocument && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
            </div>
            <CardDescription className="text-sm mt-1 min-h-[2.5rem]">
              {description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Document already uploaded state */}
        {hasDocument && !selectedFile ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <FileText className="h-8 w-8 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  Document uploaded
                </p>
                {uploadedAt && (
                  <p className="text-xs text-muted-foreground">
                    Uploaded {format(parseISO(uploadedAt), 'MMM d, yyyy')}
                  </p>
                )}
                {expiryDate && (
                  <div className="flex items-center gap-1 mt-1">
                    {isExpired ? (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Expired
                      </Badge>
                    ) : expiryWarning ? (
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Expires {format(parseISO(expiryDate), 'MMM d, yyyy')}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Expires {format(parseISO(expiryDate), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {onView && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onView}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  View
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById(`file-input-${documentType}`)?.click()}
                disabled={disabled || uploading}
                className="flex-1"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Replace
              </Button>
            </div>
            <input
              id={`file-input-${documentType}`}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </div>
        ) : selectedFile ? (
          /* File selected, ready to upload */
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <FileText className="h-8 w-8 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={cancelSelection}
                disabled={uploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Expiry date picker for CGL insurance */}
            {requiresExpiryDate && (
              <div className="space-y-2">
                <Label>Insurance Expiry Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !selectedExpiryDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedExpiryDate ? format(selectedExpiryDate, 'PPP') : 'Select expiry date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedExpiryDate}
                      onSelect={setSelectedExpiryDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={uploading || (requiresExpiryDate && !selectedExpiryDate)}
              className="w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </>
              )}
            </Button>
          </div>
        ) : (
          /* Empty state - drop zone */
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
              isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
              disabled && 'cursor-not-allowed'
            )}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !disabled && document.getElementById(`file-input-empty-${documentType}`)?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              PDF, JPG, PNG (max 10MB)
            </p>
            <input
              id={`file-input-empty-${documentType}`}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileInputChange}
              disabled={disabled}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
